import { MaterialsListMaterial } from '@/api/queries/project/useGetProjectMaterialListSummaryItems';
import { ProjectStockSource } from '@/api/queries/project/useGetProjectStockSources';
import OrderSourceSelectionField, {
  encodeStockLocationSourceValue,
  encodeSupplierSourceValue,
  OrderSourceSelection,
} from '@/ui/components/createPurchaseOrderSidebar/components/OrderSourceSelectionField';
import {
  FormStageStage,
  PurchaseOrderMaterial,
  TFormState,
} from '@/ui/components/createPurchaseOrderSidebar/CreatePurchaseOrderSidebar';
import {
  MaterialCardPropsWithTransition,
  MaterialCardWithTransition,
} from '@/ui/components/materialCard/MaterialCard';
import MaterialTagFilter, {
  toMaterialTagOptions,
} from '@/ui/components/materialTagFilter/MaterialTagFilter';
import { parseFulfillments } from '@/ui/pages/projectPage/components/MaterialsPanel/LiveAvailabilityMaterialSupplierBadge/fulfillment';
import LiveBranchStockMaterialSupplierBadge from '@/ui/pages/projectPage/components/MaterialsPanel/LiveAvailabilityMaterialSupplierBadge/LiveBranchStockMaterialSupplierBadge';
import MaterialSuppliersBadges from '@/ui/pages/projectPage/components/MaterialSuppliersBadges';
import { Button, Conditional, EmptyState } from '@payaca/ambrosia';
import List from '@payaca/components/list/List';

import { useScroll } from '@payaca/hooks';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import useGetMe from '../../../../api/queries/me/useGetMe';
import { GetProjectMaterialsListOrderSummaryItemsQuery } from '../../../../gql/graphql';

// check supplier and account support direct purchase order
const isDirectPurchaseOrderSupportedForSupplierAndAccount = (
  supplier?: GetProjectMaterialsListOrderSummaryItemsQuery['projectById']['materialsList']['materialsListOrderSummaryItems']['items'][0]['materialsListMaterial']['material']['suppliedBy'][0]['supplier']
) => {
  return (
    supplier &&
    supplier?.isDirectPurchaseOrderSupported &&
    !!supplier?.accountConnectionMetadata && // bit crude but only relevant for cp now
    supplier.accountConnectionMetadata.accountType === 'credit'
  );
};
export interface IProps {
  formStage: FormStageStage;
  materialListMaterials: MaterialsListMaterial[];
  stockSources: ProjectStockSource[];
  destinationMaterialsListPublicId?: string;
  onAction: () => void;
  actionDisabled?: boolean;
  hideList?: boolean;
}

const MaterialsToOrderList: FC<IProps> = (props) => {
  const {
    formStage,
    materialListMaterials,
    stockSources,
    destinationMaterialsListPublicId,
    onAction,
    actionDisabled = false,
    hideList = false,
  } = props;
  const { data: me } = useGetMe();

  /**
   * state
   */
  const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);
  const { y: scrollY } = useScroll({ current: scrollRef });

  /**
   * use form methods
   */
  const formMethods = useFormContext<TFormState>();
  const selectedSupplierId = formMethods.watch('supplierId');
  const selectedTagIds = formMethods.watch('tagIds');
  const selectedMaterials = formMethods.watch('materials');
  const selectedSourceListId = formMethods.watch('fromMaterialsListId');
  const isStockTransfer = formMethods.watch('type') === 'stock-transfer';
  const hasSelectedSource = !!selectedSupplierId || isStockTransfer;

  useEffect(() => {
    // The supplier path owns this auto-fill; the stock-transfer path fills its
    // own materials on selection, so leave them untouched here.
    if (formMethods.getValues('type') === 'stock-transfer') {
      return;
    }
    if (selectedSupplierId) {
      const filteredMaterialListMaterials = materialListMaterials.filter(
        (materialListMaterial) => {
          const conditions = [];

          conditions.push(
            materialListMaterial.materialsListMaterial.material.suppliedBy.some(
              (suppliedBy) =>
                suppliedBy.supplier.id === selectedSupplierId.toString()
            )
          );

          if (selectedTagIds?.length) {
            conditions.push(
              materialListMaterial.materialsListMaterial.material.tags.some(
                (tag) => selectedTagIds.includes(+tag.id)
              )
            );
          }

          return conditions.every(Boolean);
        }
      );

      formMethods.setValue(
        'materials',
        filteredMaterialListMaterials.map((filteredMaterialListMaterial) => ({
          ...filteredMaterialListMaterial,
          purchaseOrderQuantity: filteredMaterialListMaterial.materialQuantity,
          selectedQuantity: filteredMaterialListMaterial.materialQuantity,
        }))
      );
    } else {
      formMethods.setValue('materials', []);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'materialListMaterials', and 'formMethods'
  }, [selectedSupplierId, selectedTagIds]);

  useEffect(() => {
    // Only the supplier path chooses between direct and indirect; a stock
    // transfer keeps its own type.
    if (formMethods.getValues('type') === 'stock-transfer') {
      return;
    }
    const selectedSupplier = allAvailableSuppliers.find(
      (s) => +s.id === selectedSupplierId
    );

    const isDirectPurchaseOrderSupported =
      isDirectPurchaseOrderSupportedForSupplierAndAccount(selectedSupplier);
    if (isDirectPurchaseOrderSupported) {
      if (me?.user.id) {
        formMethods.setValue('userContactId', me.user.id);
      }
      formMethods.setValue('type', 'direct-asap');
    } else {
      formMethods.setValue('type', 'indirect');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'me.user.id', 'allAvailableSuppliers', and 'formMethods'
  }, [selectedSupplierId]);

  const allAvailableSuppliers = materialListMaterials.flatMap(
    (materialListMaterial) => {
      return materialListMaterial.materialsListMaterial.material.suppliedBy.map(
        (suppliedBy) => suppliedBy.supplier
      );
    }
  );

  const allAvailableTags = toMaterialTagOptions(
    materialListMaterials
      .filter((materialListMaterial) =>
        materialListMaterial.materialsListMaterial.material.suppliedBy.some(
          (suppliedBy) => {
            if (!selectedSupplierId) {
              return false;
            }

            return suppliedBy.supplier.id === selectedSupplierId.toString();
          }
        )
      )
      .flatMap(
        (materialListMaterial) =>
          materialListMaterial.materialsListMaterial.material.tags
      )
  );

  // The suppliers on the Project's outstanding materials, deduped, for the
  // source picker (matching the supplier set the picker offered before).
  // Memoised so the picker's option-list memo isn't defeated by a new array
  // identity each render.
  const sourceSuppliers = useMemo(
    () => [
      ...new Map(
        materialListMaterials
          .flatMap((materialListMaterial) =>
            materialListMaterial.materialsListMaterial.material.suppliedBy.map(
              (suppliedBy) => suppliedBy.supplier
            )
          )
          .map((supplier) => [
            +supplier.id,
            { id: +supplier.id, name: supplier.name },
          ])
      ).values(),
    ],
    [materialListMaterials]
  );

  const getSelectedSourceValue = () => {
    if (selectedSupplierId) {
      return encodeSupplierSourceValue(selectedSupplierId);
    }
    if (selectedSourceListId) {
      return encodeStockLocationSourceValue(selectedSourceListId);
    }
    return undefined;
  };
  const selectedSourceValue = getSelectedSourceValue();

  // The selected source spans several interdependent form fields (supplierId vs
  // the from/to List ids, the order type, the auto-filled materials), so rather
  // than a single Controller this writes them together here — mirroring how the
  // wizard's existing supplier effects drive the same fields via setValue.
  const handleSourceChange = (selection: OrderSourceSelection | null) => {
    if (!selection) {
      formMethods.resetField('supplierId');
      formMethods.resetField('fromMaterialsListId');
      formMethods.resetField('toMaterialsListId');
      formMethods.setValue('type', 'indirect');
      formMethods.setValue('materials', []);
      return;
    }

    if (selection.kind === 'supplier') {
      formMethods.resetField('fromMaterialsListId');
      formMethods.resetField('toMaterialsListId');
      // Back to a supplier value so the supplier effects re-run and fill the
      // materials / pick direct vs indirect.
      formMethods.setValue('type', 'indirect');
      formMethods.setValue('supplierId', selection.supplierId);
      return;
    }

    // Stock location: auto-fill the movable materials, each capped at the
    // backend's maxQuantity (min of on-hand and still-required), with no
    // further wizard step — the bottom button confirms the move.
    if (!destinationMaterialsListPublicId) {
      return;
    }
    const { stockSource } = selection;
    const sourceByMaterialId = new Map(
      stockSource.materials.map((material) => [material.material.id, material])
    );
    const filledMaterials = materialListMaterials.flatMap(
      (materialListMaterial) => {
        const source = sourceByMaterialId.get(
          materialListMaterial.materialsListMaterial.material.id
        );
        if (!source) return [];
        return [
          {
            ...materialListMaterial,
            selectedQuantity: source.maxQuantity,
            purchaseOrderQuantity: source.maxQuantity,
            quantityCap: source.maxQuantity,
          } satisfies PurchaseOrderMaterial,
        ];
      }
    );

    formMethods.resetField('supplierId');
    formMethods.setValue('tagIds', []);
    formMethods.setValue('type', 'stock-transfer');
    formMethods.setValue(
      'fromMaterialsListId',
      stockSource.materialsList.publicId
    );
    formMethods.setValue('toMaterialsListId', destinationMaterialsListPublicId);
    formMethods.setValue('materials', filledMaterials);
  };

  const hasBottomScrollShadow = !(
    scrollRef &&
    scrollRef.scrollHeight - scrollRef.scrollTop === scrollRef.clientHeight
  );

  const actionButtonDisabled = actionDisabled || selectedMaterials.length === 0;
  const panelOnLhs =
    formStage === 'INDIRECT_PURCHASE_ORDER_DETAILS' ||
    formStage === 'FULFILLMENT_OPTIONS';

  return (
    <div
      className={clstx(
        'z-10 overflow-auto flex flex-col h-full transition-colors duration-700 border-l',
        formStage === 'MATERIAL_LIST_MATERIALS' && 'bg-gray-50',
        panelOnLhs && 'bg-white'
      )}
      ref={setScrollRef}
    >
      <div
        className={clstx(
          'p-3.5 z-10 bg-white transition-colors duration-700 sticky top-0',
          formStage === 'MATERIAL_LIST_MATERIALS' && 'bg-gray-50',
          panelOnLhs && 'bg-white',
          scrollY > 0 && 'shadow',
          hideList && 'hidden'
        )}
      >
        <OrderSourceSelectionField
          canChangeSource={formStage === 'MATERIAL_LIST_MATERIALS'}
          suppliers={sourceSuppliers}
          stockSources={stockSources}
          selectedValue={selectedSourceValue}
          onChange={handleSourceChange}
        />

        <Conditional condition={!!selectedSupplierId && !isStockTransfer}>
          <Controller
            control={formMethods.control}
            name="tagIds"
            render={({ field: { value, onChange } }) => {
              return (
                <MaterialTagFilter
                  value={value ?? []}
                  onChange={onChange}
                  availableTags={allAvailableTags}
                />
              );
            }}
          />
        </Conditional>

        <Conditional condition={selectedMaterials.length !== 0}>
          <div className="flex justify-between items-center">
            <h4>Materials to include on order</h4>

            <Conditional condition={panelOnLhs}>
              <Button
                variant="link"
                disabled={actionButtonDisabled}
                onClick={actionButtonDisabled ? undefined : onAction}
              >
                Edit list
              </Button>
            </Conditional>
          </div>
        </Conditional>
      </div>
      <List
        className="px-3.5"
        items={selectedMaterials}
        item={(material) => {
          const orderedMaterialSuppliers =
            material.materialsListMaterial.material.suppliedBy.sort((a, b) => {
              if (!selectedSupplierId) {
                return 0;
              }

              return a.supplier.id === selectedSupplierId.toString() ? -1 : 1;
            });

          let materialCardProps: MaterialCardPropsWithTransition = {
            show: material.selectedQuantity > 0,
            afterLeave: () => {
              formMethods.setValue(
                'materials',
                selectedMaterials.filter(
                  (selectedMaterial) => selectedMaterial.selectedQuantity > 0
                )
              );
            },
            name: material.materialsListMaterial.material.name,
            price: orderedMaterialSuppliers[0]?.price.unitPriceExcTax,
            tags: material.materialsListMaterial.material.tags,
            thumbnailUrl:
              material.materialsListMaterial.material.image?.thumbnailUrl ||
              undefined,
          };

          if (formStage === 'MATERIAL_LIST_MATERIALS') {
            // For a stock transfer the cap is the movable quantity (min of
            // on-hand and still-required); otherwise it is the still-required
            // quantity.
            const maxQuantity =
              material.quantityCap ?? material.materialQuantity;
            materialCardProps = {
              ...materialCardProps,
              quantity: material.selectedQuantity,
              maxQuantity,
              quantityAriaLabel: `Quantity for ${material.materialsListMaterial.material.name}`,
              onChangeQuantity: (newQuantity) => {
                if (newQuantity > maxQuantity || newQuantity < 0) {
                  return;
                }

                formMethods.setValue(
                  'materials',
                  selectedMaterials.map((selectedMaterial) => {
                    return selectedMaterial.id === material.id
                      ? {
                          ...selectedMaterial,
                          selectedQuantity: newQuantity,
                          purchaseOrderQuantity: newQuantity,
                        }
                      : selectedMaterial;
                  })
                );
              },
            };
          }

          return (
            <MaterialCardWithTransition
              key={material.id}
              childrenClassName={clstx(
                panelOnLhs && 'w-full flex justify-between'
              )}
              {...materialCardProps}
            >
              <MaterialSuppliersBadges
                suppliers={orderedMaterialSuppliers.map((s) => ({
                  id: s.supplier.id,
                  name: s.supplier.name,
                }))}
                LiveAvailabilityMaterialSupplierBadge={
                  LiveBranchStockMaterialSupplierBadge
                }
                maxSuppliersToShow={1}
                fulfillments={parseFulfillments(
                  material.materialsListMaterial.fulfillments
                )}
              />

              <Conditional condition={panelOnLhs}>
                <p>Qty: {material.purchaseOrderQuantity}</p>
              </Conditional>
            </MaterialCardWithTransition>
          );
        }}
      />
      <Conditional condition={!hasSelectedSource}>
        <EmptyState
          className="mb-auto"
          text="Start by selecting a source"
          iconName="arrow-block-up.3"
        />
      </Conditional>
      <Conditional
        condition={hasSelectedSource && selectedMaterials.length === 0}
      >
        <EmptyState className="my-auto" text="No materials on order" />
      </Conditional>
      <Conditional condition={formStage === 'MATERIAL_LIST_MATERIALS'}>
        <div
          className={clstx(
            'p-3.5 z-10 transition-colors duration-700 sticky bottom-0 shadow bg-gray-50',
            hasBottomScrollShadow && 'shadow-flipped-lg',
            hasSelectedSource && selectedMaterials.length > 0 && 'mt-auto'
          )}
        >
          <Button
            className="w-full"
            disabled={actionButtonDisabled}
            // A stock transfer has no further step: the button submits the
            // form, moving the stock. The supplier path advances a stage.
            type={isStockTransfer ? 'submit' : 'button'}
            onClick={
              isStockTransfer || actionButtonDisabled ? undefined : onAction
            }
          >
            {isStockTransfer ? 'Move stock' : 'Proceed'}
          </Button>
        </div>
      </Conditional>
    </div>
  );
};

export default MaterialsToOrderList;
